import { useAtom } from 'jotai';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { openViewLogModalAtom } from '../components/device-logs-modal/DeviceLogModal.atom';
import { DeviceLogsModal } from '../components/device-logs-modal/DeviceLogsModal.ctrl';
import type { DeviceConnectionStatusEnum } from '../domain/models/deviceInfo.model';

export interface UseDeviceLogsModalReturn {
  readonly deviceLogsComponent: ReactNode;
  readonly handleShowDeviceLogsModal: () => void;
}

export interface UseDeviceLogsModalProps {
  readonly logicalDeviceId: string;
  readonly connectionStatus?: DeviceConnectionStatusEnum;
  readonly refetchIsInProgress?: () => void;
}

export function useDeviceLogsModal({
  logicalDeviceId,
  connectionStatus,
  refetchIsInProgress = () => void 0,
}: UseDeviceLogsModalProps): UseDeviceLogsModalReturn {
  const [isModalOpened, setIsModalOpened] = useAtom(openViewLogModalAtom);

  const handleShowDeviceLogsModal = useCallback(() => {
    setIsModalOpened(true);
  }, [setIsModalOpened]);

  const handleCloseDeviceLogsModal = useCallback(() => {
    setIsModalOpened(false);
  }, [setIsModalOpened]);

  const deviceLogsComponent = useMemo(
    () => (
      <DeviceLogsModal
        isOpen={isModalOpened}
        handleClose={handleCloseDeviceLogsModal}
        logicalDeviceId={logicalDeviceId}
        connectionStatus={connectionStatus}
        refetchIsInProgress={refetchIsInProgress}
      />
    ),
    [isModalOpened, handleCloseDeviceLogsModal, logicalDeviceId, connectionStatus, refetchIsInProgress],
  );

  return {
    deviceLogsComponent,
    handleShowDeviceLogsModal,
  };
}
